import React, { useState, useEffect } from 'react'
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer'
import { Download, Eye, X, FileText } from 'lucide-react'
import InvoiceTemplate from './InvoiceTemplate'
import { adminAPI, userAPI } from '../services/api'

// Import interfaces from InvoiceTemplate
interface InvoiceItem {
  description: string
  quantity: number
  unit_price: number
  total: number
}

interface CompanyInfo {
  name: string
  address: string
  city: string
  postal_code: string
  country: string
  vat_number?: string
  email: string
  phone?: string
  logo_url?: string
}

interface CustomerInfo {
  name: string
  company_name?: string
  customer_type: 'individual' | 'company'
  street_address: string
  city: string
  postal_code: string
  country: string
  vat_number?: string
  email: string
  phone?: string
}

interface InvoiceData {
  invoice_number: string
  issue_date: string
  due_date: string
  payment_date?: string
  company_info: CompanyInfo
  customer_info: CustomerInfo
  items: InvoiceItem[]
  subtotal: number
  tax_rate: number
  tax_amount: number
  total: number
  currency: string
  payment_method: string
  notes?: string
}

interface Payment {
  id: number
  user_id: number
  user_email: string
  user_name: string
  amount: number
  currency: string
  status: 'pending' | 'completed' | 'failed' | 'refunded'
  payment_method: string
  stripe_payment_id?: string
  created_at: string
  plan_name?: string
  subscription_id?: string
  subscription_status?: 'active' | 'canceled' | 'pending' | 'expired'
  next_billing?: string
}

interface InvoiceGeneratorProps {
  payment: Payment
  isOpen: boolean
  onClose: () => void
}

const InvoiceGenerator: React.FC<InvoiceGeneratorProps> = ({ payment, isOpen, onClose }) => {
  const [showPreview, setShowPreview] = useState(true)
  const [companyData, setCompanyData] = useState<CompanyInfo | null>(null)
  const [customerBillingInfo, setCustomerBillingInfo] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (isOpen) {
      fetchCompanyData()
      fetchCustomerBillingInfo()
    }
  }, [isOpen])

  const fetchCompanyData = async () => {
    try {
      const response = await adminAPI.getPublicCompanyData()
      const companyInfo = response.data.data // Rozpakowujemy data.data
      setCompanyData({
        name: companyInfo.company_name || "PresentClic Inc.",
        address: companyInfo.street || "123 Business Street",
        city: companyInfo.city || "New York", 
        postal_code: companyInfo.postal_code || "10001",
        country: companyInfo.country === 'PL' ? 'Poland' : companyInfo.country || "United States",
        vat_number: companyInfo.nip || "",
        email: companyInfo.email || "billing@presentclic.com",
        phone: companyInfo.phone || "",
        logo_url: companyInfo.logo_url ? `https://panel.presentclic.com${companyInfo.logo_url}` : ""
      })
    } catch (error: any) {
      console.error('Failed to fetch company data:', error)
      // Fallback to default company data if public endpoint fails
      setCompanyData({
        name: "PresentClic Inc.",
        address: "123 Business Street",
        city: "New York",
        postal_code: "10001",
        country: "United States",
        vat_number: "",
        email: "billing@presentclic.com",
        phone: "",
        logo_url: ""
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchCustomerBillingInfo = async () => {
    try {
      const response = await userAPI.getBillingInfo()
      setCustomerBillingInfo(response.data)
    } catch (error) {
      console.error('Failed to fetch customer billing info:', error)
      // Keep null for customers without billing info
    }
  }

  if (!isOpen) return null
  if (loading) return <div className="text-white">Loading company data...</div>

  const getCompanyInfo = (): CompanyInfo => {
    return companyData!
  }

  // Get customer billing info
  const getCustomerInfo = (): CustomerInfo => {
    // Use billing info from API if available, otherwise fallback to basic user data
    if (customerBillingInfo) {
      return {
        name: payment.user_name,
        company_name: customerBillingInfo.company_name || '',
        customer_type: customerBillingInfo.customer_type || 'individual',
        street_address: customerBillingInfo.street_address || '',
        city: customerBillingInfo.city || '',
        postal_code: customerBillingInfo.postal_code || '',
        country: customerBillingInfo.country || '',
        vat_number: customerBillingInfo.vat_number || '',
        email: payment.user_email,
        phone: customerBillingInfo.phone || ''
      }
    }
    
    // Fallback for customers without billing info
    return {
      name: payment.user_name,
      company_name: '',
      customer_type: 'individual',
      street_address: '',
      city: '',
      postal_code: '',
      country: '',
      vat_number: '',
      email: payment.user_email,
      phone: ''
    }
  }

  // Generate invoice data
  const generateInvoiceData = (): InvoiceData => {
    const dueDate = new Date()
    dueDate.setDate(dueDate.getDate() + 30)

    // Calculate tax (assuming 0% for digital services, adjust as needed)
    const taxRate = 0.0 // 0% tax rate
    const subtotal = payment.amount
    const taxAmount = subtotal * taxRate
    const total = subtotal + taxAmount

    const items: InvoiceItem[] = [
      {
        description: `${payment.plan_name || 'PresentClic Subscription'} - Digital Service`,
        quantity: 1,
        unit_price: payment.amount,
        total: payment.amount
      }
    ]

    return {
      invoice_number: `INV-${new Date().getFullYear()}-${String(payment.id).padStart(6, '0')}`,
      issue_date: payment.created_at,
      due_date: dueDate.toISOString(),
      payment_date: payment.status === 'completed' ? payment.created_at : undefined,
      company_info: getCompanyInfo(),
      customer_info: getCustomerInfo(),
      items,
      subtotal,
      tax_rate: taxRate,
      tax_amount: taxAmount,
      total,
      currency: payment.currency,
      payment_method: payment.payment_method.replace('stripe_', '').toUpperCase(),
      notes: payment.status === 'completed' ? 'Thank you for your payment!' : undefined
    }
  }

  const invoiceData = generateInvoiceData()
  const fileName = `${invoiceData.invoice_number}-${(payment.user_name || 'customer').replace(/\s+/g, '-')}.pdf`

  const handleGenerateInvoice = async () => {
    try {
      // TODO: Save invoice to database
      // await invoiceAPI.createInvoice(invoiceData)
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      console.log('Invoice generated:', invoiceData)
    } catch (error) {
      console.error('Error generating invoice:', error)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <div className="inline-block align-bottom bg-dark-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-dark-800 px-6 pt-6 pb-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium text-white flex items-center">
                <FileText className="h-6 w-6 mr-2 text-primary" />
                Generate Invoice
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Invoice Summary */}
            <div className="bg-dark-700 rounded-lg p-4 mb-6">
              <h4 className="text-lg font-medium text-white mb-4">Invoice Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Invoice Number:</span>
                  <span className="text-white ml-2 font-mono">{invoiceData.invoice_number}</span>
                </div>
                <div>
                  <span className="text-gray-400">Customer:</span>
                  <span className="text-white ml-2">{payment.user_name}</span>
                </div>
                <div>
                  <span className="text-gray-400">Amount:</span>
                  <span className="text-white ml-2 font-semibold">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: payment.currency
                    }).format(payment.amount)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Plan:</span>
                  <span className="text-white ml-2">{payment.plan_name || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-400">Payment Status:</span>
                  <span className={`ml-2 px-2 py-1 rounded text-xs ${
                    payment.status === 'completed' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Date:</span>
                  <span className="text-white ml-2">
                    {new Date(payment.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Customer Billing Info */}
            <div className="bg-dark-700 rounded-lg p-4 mb-6">
              <h4 className="text-lg font-medium text-white mb-4">Billing Address</h4>
              {invoiceData.customer_info.street_address === 'Address not provided' ? (
                <div className="text-yellow-400 text-sm">
                  ⚠️ Customer has not provided billing address. The invoice will show default placeholder address.
                  Customer can update their billing information in their profile.
                </div>
              ) : (
                <div className="text-sm text-gray-300">
                  <div>{invoiceData.customer_info.customer_type === 'company' && invoiceData.customer_info.company_name}</div>
                  <div>{invoiceData.customer_info.name}</div>
                  <div>{invoiceData.customer_info.street_address}</div>
                  <div>{invoiceData.customer_info.city}, {invoiceData.customer_info.postal_code}</div>
                  <div>{invoiceData.customer_info.country}</div>
                  {invoiceData.customer_info.vat_number && <div>VAT: {invoiceData.customer_info.vat_number}</div>}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between space-x-4">
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <Eye className="h-4 w-4" />
                  <span>{showPreview ? 'Hide Preview' : 'Show Preview'}</span>
                </button>

                <PDFDownloadLink
                  document={<InvoiceTemplate data={invoiceData} />}
                  fileName={fileName}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  onClick={handleGenerateInvoice}
                >
                  {({ loading }) => (
                    <>
                      <Download className="h-4 w-4" />
                      <span>{loading ? 'Generating...' : 'Download PDF'}</span>
                    </>
                  )}
                </PDFDownloadLink>
              </div>

              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>

            {/* PDF Preview */}
            {showPreview && (
              <div className="mt-6 border border-dark-600 rounded-lg overflow-hidden" style={{ height: '600px' }}>
                <PDFViewer width="100%" height="100%">
                  <InvoiceTemplate data={invoiceData} />
                </PDFViewer>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default InvoiceGenerator